import { createHash } from "node:crypto";
import type { LearningCharter, SelfGrillQuestionId } from "./learningCharter";
import { buildRetroGrillBatch, type WinVerdict } from "./retroGrill";
import type { Outcome } from "./types";
import { DAY_MS } from "./window";
import { WRITE_TIERS, type AgentProposableCategory } from "./writeTiers";

/**
 * W2 LEARNER (B1, B2, B4, B6, B7, B15, B16; Agent Brains 5.9). The pure half of
 * the weekly learner: every count, rate, interval, threshold and status in the
 * learner is computed here, in TypeScript. The model (agents/learnModel.ts) may
 * only map text to an id in a closed catalog or phrase prose from figures this
 * file computed; it never decides anything below.
 *
 * WHAT THIS IS NOT. It never writes anything, never reads the clock, and has no
 * path to a T3 value: the learner never edits its own objective (CLAUDE.md
 * section 3). The only numbers it takes from the Learning Charter are bars it
 * READS (`patternMinSample`, `expiryDays`, the floors, the needs); the
 * tighten-only `barMultiplier` is applied here to the sample bar, never written
 * back to the charter (open question 5), and nothing here loosens one.
 *
 * THE GATE ORDER (design W2, each failure stops that proposal with a status):
 *   G0 needs manifest, G1 tier guard (db), G2 testable predicate, G3
 *   pre-registration (confirm in a LATER week than registration, evidence only
 *   from threads closed after it), G4 sample, G5 strata, G6 floors, G7
 *   self-grill, G8 collisions, G9 citations, G10 route, G11 monitor.
 */

// ---------------------------------------------------------------------------
// Constants. D30 is unruled: every placeholder number is AWAITING RATIFICATION.
// ---------------------------------------------------------------------------

/** D30: at most this many open hypotheses per client. AWAITING RATIFICATION. */
export const LEARN_MAX_OPEN_HYPOTHESES = 10;
/** D30: false-discovery rate for the batch, whole percent (Benjamini-Hochberg). AWAITING RATIFICATION. */
export const LEARN_FDR_PERCENT = 10;
/** D30: threads per side in a stratum before a result there counts as VALIDATED (not merely provisional). AWAITING RATIFICATION. */
export const LEARN_STRATUM_VALIDATE_MIN = 20;
/** D30: threads per side in one cell before the cell may reverse (kill) a hypothesis. AWAITING RATIFICATION. */
export const LEARN_CELL_MIN_PER_SIDE = 5;
/** B15.1: one contact above this share of the evidence confounds it (design G7, "over 40%"). AWAITING RATIFICATION. */
export const LEARN_CONTACT_CONCENTRATION_MAX = 0.4;
/** Total-variation distance between the with-group's and the without-group's mix over a dimension above which the dimension confounds. AWAITING RATIFICATION. */
export const LEARN_MIX_TVD_MAX = 0.5;
/** Threads per side in one counter-test cell. AWAITING RATIFICATION. */
export const LEARN_COUNTER_MIN_PER_SIDE = 3;
/** Threads with the predicate seen in the registration week before it may be pre-registered. AWAITING RATIFICATION. */
export const LEARN_REGISTER_MIN_WITH = 3;
/**
 * Q5: the tighten-only multiplier mechanism on the sample bar (`barMultiplier`, never below 1).
 * Review B1: stale or missing owner ratings now HALT in every mode, so nothing raises it today;
 * the constant stays for a future ruling that wants a raised bar. AWAITING RATIFICATION.
 */
export const LEARN_RATINGS_STALE_BAR_MULTIPLIER = 2;
/** M3: an open hypothesis is closed as `expired` after this many weeks without a verdict, so the open cap frees. AWAITING RATIFICATION. */
export const HYPOTHESIS_MAX_OPEN_WEEKS = 8;
/** A floor metric may be this many points worse than its frozen baseline before it breaches. AWAITING RATIFICATION. */
export const LEARN_FLOOR_TOLERANCE_PP = 5;
/** B15.5: total-variation distance, week's routed scenario mix against the eval set, above which an eval-refresh proposal is queued. AWAITING RATIFICATION. */
export const LEARN_EVAL_MIX_TVD_MAX = 0.25;
/** First reply inside this window counts as a fast response (the response_speed counter). AWAITING RATIFICATION. */
export const LEARN_FAST_RESPONSE_MS = 60 * 60 * 1000;

/** The six counter-hypotheses B15.2 lets a self-grill name (design G7). */
export const COUNTER_CODES = [
  "season",
  "price_band",
  "single_contact",
  "inquiry_mix",
  "response_speed",
  "owner_typed_not_agent",
] as const;
export type CounterCode = (typeof COUNTER_CODES)[number];

// ---------------------------------------------------------------------------
// Types (the design's interfaces)
// ---------------------------------------------------------------------------

/**
 * The one list of predicate kinds. The type, the registration row (eventCatalog.ts) and the stored-row reader
 * (db/learnerEvidence.ts) all take it from here, so a kind added in one place is added in all three.
 * `reply_trait` (TA-1360): a trait of the shop's reply (a closer, a length), read off the reply-chance model,
 * judged on write-backs.
 */
export const PREDICATE_KINDS = ["pattern_def", "fact_set", "stamp_code", "key_moment", "silence", "reply_trait"] as const;
export type PredicateKind = (typeof PREDICATE_KINDS)[number];
/** A closed-catalog test. `key` and `value` are ids and codes, never text. */
export interface Predicate {
  readonly kind: PredicateKind;
  readonly key: string;
  readonly value: string;
}

export type FloorMetric =
  | "complaint_rate"
  | "escalation_rate"
  | "continuation_rate"
  | "review_rating"
  | "first_pass_grounding_percent"
  /** G68 (MS B14): the share of threads whose decided drafts carried an AI tell (drafts.ai_tells). */
  | "ai_tell_rate"
  /** R7: no sentiment source exists, so it is listed not measured on every reading, never passed. */
  | "sentiment";

/**
 * The metrics an invalidator may name: exactly the `hypothesis.registered` catalog enum (eventCatalog.ts).
 * D13 fix round 1: G68's ai_tell_rate and R7's sentiment are floors only, never invalidators, so widening
 * FloorMetric cannot let a registration carry a metric its catalog row refuses (pinned in
 * eventCatalog.learner.test.ts). Admitting one is a catalog change first.
 */
export const INVALIDATOR_METRICS = [
  "complaint_rate",
  "escalation_rate",
  "continuation_rate",
  "review_rating",
  "first_pass_grounding_percent",
  "lift_pp",
] as const satisfies readonly (FloorMetric | "lift_pp")[];

export interface Invalidator {
  readonly metric: (typeof INVALIDATOR_METRICS)[number];
  readonly comparator: "lt" | "gt";
  readonly threshold: number;
}

/**
 * The one list of hypothesis sources, shared like `HYPOTHESIS_TARGETS`: the type, the catalog's
 * `hypothesis.registered` enum and learnerEvidence's row check all read it. `restraint` (G72, TA-1294,
 * B3): a silence seed, one per restraint predicate off the restraint ledger (waveWInputs). `reply_model`
 * (TA-1360): a reply trait the reply-chance model scored (analyze:reply-model), judged on `replied`.
 */
export const HYPOTHESIS_SOURCES = [
  "playbook_def",
  "pattern_candidate",
  "retro_grill_win",
  "owner_note",
  "edit_signal",
  "lesson",
  "miner_model",
  "restraint",
  "reply_model",
] as const;
export type HypothesisSource = (typeof HYPOTHESIS_SOURCES)[number];

/**
 * The one list of hypothesis targets, shared like `PREDICATE_KINDS`. `replied` (TA-1360): the customer wrote
 * back within a day of the shop's first reply (`wroteBackWithinDay`). Not a sale target; see `measurableFor`.
 */
export const HYPOTHESIS_TARGETS = ["won", "consult_booked", "deposit", "replied"] as const;
export type HypothesisTarget = (typeof HYPOTHESIS_TARGETS)[number];

export interface Hypothesis {
  readonly id: string;
  readonly clientId: string;
  readonly category: AgentProposableCategory;
  readonly source: HypothesisSource;
  readonly sourceIds: readonly string[];
  /** Orders attention, never evidence. */
  readonly priority: number;
  readonly predicate: Predicate;
  readonly target: HypothesisTarget;
  readonly scenarios: readonly string[];
  readonly registeredWeek: string;
  readonly registeredAt: string;
  readonly predictedLiftPp: number;
  readonly invalidators: readonly Invalidator[];
  readonly reviewBy: string;
  /**
   * Review B2 (G3): the ids of the threads the registration week was read from. Ids only. A thread
   * listed here can never be this hypothesis's confirming evidence, whatever happens to its outcome later.
   * Absent on a row written before this field: the first-decided-time check still applies.
   */
  readonly registrationThreadIds?: readonly string[];
  /**
   * Review B4 (G6): the floor baseline counts, frozen at registration (counts only). Absent or null
   * means the floors cannot be measured for this hypothesis, which holds its proposal.
   */
  readonly baseline?: FloorBaseline | null;
}

export interface ThreadOutcomeView {
  readonly threadId: string;
  readonly contactId: string;
  /**
   * ISO. The moment the outcome that closed the thread was recorded. TA-1361: on a view of the `replied`
   * population (`ThreadPopulations.replied`), the moment the first shop reply run ended, which is what that
   * population's window keys on. There `firstDecidedAt` is the moment the run STARTED (fix round 1): a start
   * never moves, so a thread whose reply began before a hypothesis registered is never its evidence, even when
   * a later shop nudge moves the run's end past registration.
   */
  readonly closedAt: string;
  /**
   * ISO. When this thread FIRST had a decided outcome, over all time. A thread decided before a
   * hypothesis registered is not evidence for it even if its outcome moves later (review B2).
   * Absent: `closedAt` is used.
   */
  readonly firstDecidedAt?: string;
  readonly outcome: Outcome;
  readonly stratum: { readonly inquiryType: string; readonly priceBand: string; readonly season: string };
  /** `kind:key:value` strings the thread satisfies. */
  readonly predicates: readonly string[];
  /** Predicate kinds this thread could not be read for (no labeler, no stamps): evidence for them is `not_available`, never inferred. */
  readonly unavailable: readonly PredicateKind[];
  /** First reply within `LEARN_FAST_RESPONSE_MS`; null when not measurable. */
  readonly fastResponse: boolean | null;
  /** The reply was typed by the owner, not the agent; null when not measurable. */
  readonly ownerTyped: boolean | null;
  readonly complaint: boolean;
  readonly escalated: boolean;
  /** The customer wrote again after the first shop reply; null when no reply went out. */
  readonly continued: boolean | null;
  /**
   * TA-1360 feed: the `replied` target's outcome, as the reply-chance model measured it (pipeline
   * replyUnits.ts): a customer message landed within 24 hours (`DAY_MS`) after the LAST message of the
   * first shop run that follows the first customer message. Null when there is no such run, or its day is
   * still open at the read's `until` with no write-back yet: not measured, never "did not write back".
   * `continued` stays for the floors.
   */
  readonly wroteBackWithinDay: boolean | null;
  /** 1 to 5; null when no rating is linked to the thread. */
  readonly reviewRating: number | null;
  /** Every draft on the thread passed the gates on the first pass; null when no draft was decided. */
  readonly firstPassClean: boolean | null;
  /**
   * G68: a decided draft on the thread carried at least one AI tell (drafts.ai_tells), false when every
   * decided draft was linted clean. A recorded tell on any decided draft reads true even when another
   * decided draft has NULL ai_tells (the tell is a fact, and it only tightens the ai_tell_rate floor); null
   * when a NULL sits beside no tell (not recorded) or no draft was decided; absent when the loader does not
   * read the column. Null and absent are not measured, never clean.
   */
  readonly aiTell?: boolean | null;
  readonly draftIds: readonly string[];
  readonly eventIds: readonly string[];
}

export interface EvidenceCell {
  readonly stratum: { readonly inquiryType: string; readonly priceBand: string; readonly season: string };
  readonly withN: number;
  readonly withWon: number;
  readonly withoutN: number;
  readonly withoutWon: number;
  /** 95% interval on the lift in percentage points (Newcombe over Wilson), or null when a side is empty. */
  readonly wilson95Pp: readonly [number, number] | null;
  readonly underpowered: boolean;
}

export const PROPOSAL_STATUSES = [
  "registered",
  "insufficient_sample",
  "not_available",
  "not_significant",
  "evidence_ok",
  "killed_stratum",
  "killed_floor",
  "confounded",
  "isolated",
  "no_ship_unanswered",
  "held_collision",
  "no_citations",
  "held_floors_unmeasured",
  "queued_owner",
  "queued_t2",
  "ratified",
  "self_retracted",
  "auto_reverted",
  "expired",
  "not_testable",
  "open_cap",
  "no_signal",
  "refused_tier",
  "same_week",
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

/** Statuses after which a hypothesis is closed and takes no further evidence. */
export const TERMINAL_STATUSES: ReadonlySet<ProposalStatus> = new Set<ProposalStatus>([
  "killed_stratum",
  "killed_floor",
  "confounded",
  "isolated",
  "held_collision",
  "queued_owner",
  "queued_t2",
  "ratified",
  "self_retracted",
  "auto_reverted",
  "expired",
]);

export const LEARN_HALTS = [
  "batch_quality_below_bar",
  "labeler_accuracy_unknown",
  "eval_set_stale",
  "owner_ratings_stale",
  "min_samples",
] as const;
/** The halts this file's manifest can raise; the four charter halts are the db's (`startLearnerBatch`). */
export type LearnHalt = (typeof LEARN_HALTS)[number];

// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------

/** ISO 8601 week of a UTC instant, `2026-W41`. Same week numbering as the digests. */
export function isoWeekKey(d: Date): string {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dow);
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((t.getTime() - yearStart) / DAY_MS + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export const predicateString = (p: Predicate): string => `${p.kind}:${p.key}:${p.value}`;

const sha = (s: string): string => createHash("sha256").update(s).digest("hex");

/** The same grouping `surfacePatternCandidates` uses (agent plus the sorted fact set), as a catalog key. */
export function factSetKey(agentId: string, factsUsed: readonly string[]): string {
  return `fs_${sha(`${agentId}::${[...factsUsed].sort().join("|")}`).slice(0, 24)}`;
}

/** Stable per (client, predicate, target, category): the same idea is one hypothesis across weeks. */
export function hypothesisIdOf(
  clientId: string,
  h: { readonly predicate: Predicate; readonly target: HypothesisTarget; readonly category: string },
): string {
  return `hyp_${sha(`${clientId}|${predicateString(h.predicate)}|${h.target}|${h.category}`).slice(0, 24)}`;
}

/**
 * What each thread reaches. The sales targets read the outcome: booked < deposit < sold. `replied` reads
 * the write-back within a day only (`wroteBackWithinDay === true`, the model's outcome), never the outcome
 * and never `continued` (any later message). A thread whose `wroteBackWithinDay` is null reaches nothing
 * here and is not measurable for it: see `measurableFor`.
 */
export function reachedTarget(t: Pick<ThreadOutcomeView, "outcome" | "wroteBackWithinDay">, target: HypothesisTarget): boolean {
  if (target === "replied") return t.wroteBackWithinDay === true;
  if (target === "won") return t.outcome === "sold";
  if (target === "deposit") return t.outcome === "deposit" || t.outcome === "sold";
  return t.outcome === "booked" || t.outcome === "deposit" || t.outcome === "sold";
}

/**
 * Whether a thread can be judged for a target at all. Only `replied` can lack a reading: with no first shop
 * run, or its day still open (`wroteBackWithinDay === null`), the thread is in neither arm, never counted as
 * "did not write back" (B13).
 */
export function measurableFor(t: Pick<ThreadOutcomeView, "wroteBackWithinDay">, target: HypothesisTarget): boolean {
  return target !== "replied" || t.wroteBackWithinDay !== null;
}

/**
 * TA-1361. The two populations a target is judged on. `decided`: threads whose latest standing outcome in
 * the window is decided (an owner tap or the ghost sweep), the sales targets' population. `replied`: every
 * thread whose first shop reply run ended in the window with its write-back day closed by the window's end,
 * whatever its outcome label says or whether it has one (db loadRepliedThreadViews). The reply-chance model
 * the `reply_model` seeds come from was fit on every reply run; judging those seeds on labelled threads only
 * read a censored sample, heavy on ghosted threads and weeks late.
 */
export interface ThreadPopulations {
  readonly decided: readonly ThreadOutcomeView[];
  readonly replied: readonly ThreadOutcomeView[];
}

/** What the judge, the registration and the monitor are handed: both populations, or one list read for every target. */
export type ThreadEvidence = readonly ThreadOutcomeView[] | ThreadPopulations;

const isPopulations = (e: ThreadEvidence): e is ThreadPopulations => !Array.isArray(e);

/**
 * TA-1361: the one rule that picks a target's threads, called by `registerHypotheses`, `evaluateHypothesis`
 * and `monitorShipped` alike, so a frozen baseline, the judge's counts and the post-ship counts all come from
 * the same population. One list is every target's.
 */
export function populationFor(evidence: ThreadEvidence, target: HypothesisTarget): readonly ThreadOutcomeView[] {
  if (!isPopulations(evidence)) return evidence;
  return target === "replied" ? evidence.replied : evidence.decided;
}

/**
 * TA-1361: whether a thread's outcome label lets it be judged for a target. A sales target needs a decided
 * outcome, so `ongoing` is dropped. `replied` is judged on the write-back, never on the label: an `ongoing`
 * thread whose customer wrote back within a day is evidence for it (`measurableFor` still drops an open day).
 */
export function labelAdmits(t: Pick<ThreadOutcomeView, "outcome">, target: HypothesisTarget): boolean {
  return target === "replied" || t.outcome !== "ongoing";
}

/** Wilson score interval for k of n, as fractions. */
export function wilson(k: number, n: number, z = 1.96): [number, number] {
  if (n <= 0) return [0, 1];
  const p = k / n;
  const denom = 1 + (z * z) / n;
  const centre = p + (z * z) / (2 * n);
  const margin = z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n));
  return [Math.max(0, (centre - margin) / denom), Math.min(1, (centre + margin) / denom)];
}

/** Newcombe's hybrid-score interval for p1 - p2, in percentage points; null when a side is empty. */
export function newcombeDiffPp(k1: number, n1: number, k2: number, n2: number): [number, number] | null {
  if (n1 <= 0 || n2 <= 0) return null;
  const p1 = k1 / n1;
  const p2 = k2 / n2;
  const [l1, u1] = wilson(k1, n1);
  const [l2, u2] = wilson(k2, n2);
  const d = p1 - p2;
  const lo = d - Math.sqrt((p1 - l1) ** 2 + (u2 - p2) ** 2);
  const hi = d + Math.sqrt((u1 - p1) ** 2 + (p2 - l2) ** 2);
  return [lo * 100, hi * 100];
}

function erf(x: number): number {
  // Abramowitz and Stegun 7.1.26, absolute error below 1.5e-7.
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
  return sign * y;
}

/** One-sided p for "the with-group rate is above the without-group rate" (pooled two-proportion z). */
export function oneSidedP(k1: number, n1: number, k2: number, n2: number): number {
  if (n1 <= 0 || n2 <= 0) return 1;
  const pooled = (k1 + k2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  if (se === 0) return k1 / n1 > k2 / n2 ? 0 : 1;
  const z = (k1 / n1 - k2 / n2) / se;
  return 1 - 0.5 * (1 + erf(z / Math.SQRT2));
}

/** Benjamini-Hochberg: the ids whose p passes at `alphaPercent` across the family. */
export function benjaminiHochberg(items: readonly { readonly id: string; readonly p: number }[], alphaPercent: number): Set<string> {
  const sorted = [...items].sort((a, b) => a.p - b.p || (a.id < b.id ? -1 : 1));
  const m = sorted.length;
  let cut = -1;
  sorted.forEach((it, i) => {
    if (it.p <= ((i + 1) / m) * (alphaPercent / 100)) cut = i;
  });
  return new Set(sorted.slice(0, cut + 1).map((i) => i.id));
}

// ---------------------------------------------------------------------------
// G0 needs manifest (B15.5)
// ---------------------------------------------------------------------------

export interface NeedsInputs {
  /** Share of the week's closed threads with a usable label (0..1); null = not measured. */
  readonly batchQuality: number | null;
  readonly labelerMode: "off" | "on";
  /** Gold accuracy of the labeler (0..1); read only when the labeler is on. */
  readonly labelerAccuracy: number | null;
  /** Age of the active eval set in days; null = none. */
  readonly evalAgeDays: number | null;
  /** Total-variation distance of the week's routed scenario mix against the eval set; null = not measured. */
  readonly evalMixTvd: number | null;
  /** Age of the newest owner rating in days; null = no source (open question 7). */
  readonly ownerRatingsAgeDays: number | null;
  /** Closed, decided, labelled threads in the batch. */
  readonly threadsLabelled: number;
}

export interface NeedsVerdict {
  readonly ok: boolean;
  readonly halts: readonly LearnHalt[];
  /** One CODE per halt, naming what was unmet. */
  readonly codes: readonly string[];
  /** Tighten-only: always >= 1. */
  readonly barMultiplier: number;
  readonly ownerRatings: "fresh" | "stale" | "none";
  /** True when the eval set's mix drifted over the bar: queue an `eval-refreshes` T2 proposal. */
  readonly evalRefreshProposed: boolean;
}

/**
 * G0. Every declared need that is unmet HALTS, owner ratings included (review
 * B1): `ratingsRecencyDays` is a charter need, "stale" or "none" adds
 * `owner_ratings_stale`, and there is no mode that raises the bar instead.
 * Everything here only tightens; `barMultiplier` stays 1 and is clamped.
 */
export function checkNeedsManifest(i: NeedsInputs, c: LearningCharter): NeedsVerdict {
  const halts: LearnHalt[] = [];
  const codes: string[] = [];
  const add = (h: LearnHalt, code: string): void => {
    if (!halts.includes(h)) halts.push(h);
    codes.push(code);
  };

  const minQ = c.needs.minBatchQuality;
  if (minQ === null) add("batch_quality_below_bar", "needs.minBatchQuality:unruled");
  else if (i.batchQuality === null) add("batch_quality_below_bar", "batch_quality:not_measured");
  else if (i.batchQuality < minQ) add("batch_quality_below_bar", "batch_quality:below_bar");

  if (i.labelerMode === "on") {
    const minA = c.needs.minLabelerAccuracy;
    if (minA === null) add("labeler_accuracy_unknown", "needs.minLabelerAccuracy:unruled");
    else if (i.labelerAccuracy === null) add("labeler_accuracy_unknown", "labeler_accuracy:not_measured");
    else if (i.labelerAccuracy < minA) add("labeler_accuracy_unknown", "labeler_accuracy:below_bar");
  }

  const maxAge = c.needs.evalFreshnessMaxDays;
  if (maxAge === null) add("eval_set_stale", "needs.evalFreshnessMaxDays:unruled");
  else if (i.evalAgeDays === null) add("eval_set_stale", "eval_set:none");
  else if (i.evalAgeDays > maxAge) add("eval_set_stale", "eval_set:older_than_bar");

  const recency = c.needs.ratingsRecencyDays;
  const ownerRatings: NeedsVerdict["ownerRatings"] = i.ownerRatingsAgeDays === null ? "none" : recency !== null && i.ownerRatingsAgeDays <= recency ? "fresh" : "stale";
  const barMultiplier = 1;
  if (ownerRatings !== "fresh") add("owner_ratings_stale", `owner_ratings:${ownerRatings}`);

  if (i.threadsLabelled < c.bars.patternMinSample) add("min_samples", "threads_labelled:below_pattern_min_sample");

  return {
    ok: halts.length === 0,
    halts,
    codes,
    barMultiplier: Math.max(1, barMultiplier),
    ownerRatings,
    evalRefreshProposed: i.evalMixTvd !== null && i.evalMixTvd > LEARN_EVAL_MIX_TVD_MAX,
  };
}

// ---------------------------------------------------------------------------
// Registration (B4: propose on N)
// ---------------------------------------------------------------------------

/** A seed whose category already passed the tier guard (db/proposalGuard). */
export interface HypothesisSeed {
  readonly source: HypothesisSource;
  readonly sourceIds: readonly string[];
  readonly category: AgentProposableCategory;
  readonly predicate: Predicate;
  readonly target: HypothesisTarget;
  readonly scenarios: readonly string[];
  readonly priority: number;
}

export interface FloorBaseline {
  readonly n: number;
  readonly complaints: number;
  readonly escalations: number;
  /** Threads with a measurable continuation, and how many continued. */
  readonly continuationN: number;
  readonly continued: number;
  readonly ratingN: number;
  readonly ratingSum: number;
  /**
   * G68: threads with an AI-tell reading, and how many carried a tell. Present only when the threads
   * carried a reading, so a baseline read from views that never load the column keeps its old shape;
   * absent (a row frozen before G68 included) means the ai_tell_rate floor is not measured.
   */
  readonly aiTellN?: number;
  readonly aiTells?: number;
  /**
   * TA-1361 fix round 1: the population that froze these counts, `replied` when a replied seed froze them from
   * `ThreadPopulations.replied`. Absent means the decided population, which every row frozen before TA-1361
   * read, a replied hypothesis's included. `frozenBaselineFor` reads it.
   */
  readonly population?: "replied";
}

/**
 * TA-1361 fix round 1 (CLAUDE.md 0, B13): the frozen baseline a target's floors may be read against, or null.
 * A `replied` target takes only a baseline the replied population froze; one frozen from the decided population
 * (a row registered before TA-1361) is no frozen baseline, so its floors read not measured and the proposal is
 * held, and the monitor says learn_no_frozen_baseline. A sales target never takes a replied population's counts.
 */
export function frozenBaselineFor(b: FloorBaseline | null, target: HypothesisTarget): FloorBaseline | null {
  if (b === null) return null;
  const replied = b.population === "replied";
  return (target === "replied") === replied ? b : null;
}

export interface RegisterContext {
  readonly clientId: string;
  readonly now: Date;
  readonly charter: LearningCharter;
  /** Every `kind:key:value` the system can compute this week (G2). */
  readonly catalog: ReadonlySet<string>;
  readonly existingIds: ReadonlySet<string>;
  readonly openCount: number;
  /**
   * The week's closed threads, used only to observe the signal at registration. TA-1361: handed as
   * populations, each seed reads its target's (`populationFor`), and its registration ids are that population's.
   */
  readonly threads: ThreadEvidence;
  /** The floor baseline a seed freezes; with populations, the `decided` population's. */
  readonly baseline: FloorBaseline | null;
  /**
   * TA-1361: with populations, the baseline a `replied` seed freezes, the `replied` population's, so the monitor's
   * post-ship counts and the frozen counts come from one rule. Absent there, null: the floors read not measured
   * and the proposal is held (B13), never the decided population's counts.
   */
  readonly repliedBaseline?: FloorBaseline | null;
}

export interface RegisterOutcome {
  readonly registered: readonly Hypothesis[];
  readonly skipped: readonly { readonly seed: HypothesisSeed; readonly id: string; readonly status: "not_testable" | "open_cap" | "no_signal" | "registered_already" }[];
}


function counts(threads: readonly ThreadOutcomeView[], p: Predicate, target: HypothesisTarget) {
  const s = predicateString(p);
  let withN = 0;
  let withWon = 0;
  let withoutN = 0;
  let withoutWon = 0;
  for (const t of threads) {
    if (!labelAdmits(t, target) || t.unavailable.includes(p.kind) || !measurableFor(t, target)) continue;
    const reached = reachedTarget(t, target);
    if (t.predicates.includes(s)) {
      withN += 1;
      if (reached) withWon += 1;
    } else {
      withoutN += 1;
      if (reached) withoutWon += 1;
    }
  }
  return { withN, withWon, withoutN, withoutWon };
}

export function baselineComplaintPct(b: FloorBaseline | null): number | null {
  return b === null || b.n < LEARN_CELL_MIN_PER_SIDE ? null : (b.complaints / b.n) * 100;
}

/**
 * Pre-register (B4 "propose on N"). A seed is registered only when its
 * predicate is in the closed catalog (G2), there is room under the open cap
 * (D30), and the week showed a positive lift on at least
 * `LEARN_REGISTER_MIN_WITH` threads. Registration claims no evidence: the
 * hypothesis is confirmed on a LATER week, on threads closed after `now`.
 * The invalidators are fixed here, before any confirming evidence exists.
 */
export function registerHypotheses(seeds: readonly HypothesisSeed[], ctx: RegisterContext): RegisterOutcome {
  const expiry = ctx.charter.bars.singleClientProvisional.expiryDays;
  if (expiry === null) throw new Error("registerHypotheses: bars.singleClientProvisional.expiryDays is unruled; the batch should have halted needs_unmet");
  const week = isoWeekKey(ctx.now);
  const ordered = [...seeds].sort((a, b) => b.priority - a.priority || (predicateString(a.predicate) < predicateString(b.predicate) ? -1 : 1));
  const registered: Hypothesis[] = [];
  const skipped: Array<RegisterOutcome["skipped"][number]> = [];
  const seen = new Set<string>(ctx.existingIds);

  for (const seed of ordered) {
    // TA-1361: the seed's own population and the baseline frozen from it. Fix round 1: a replied population's
    // baseline is tagged with it (`frozenBaselineFor`).
    const threads = populationFor(ctx.threads, seed.target);
    const onReplied = seed.target === "replied" && isPopulations(ctx.threads);
    const repliedBase = ctx.repliedBaseline ?? null;
    const baseline = onReplied ? (repliedBase === null ? null : { ...repliedBase, population: "replied" as const }) : ctx.baseline;
    const complaintBase = baselineComplaintPct(baseline);
    const id = hypothesisIdOf(ctx.clientId, seed);
    if (seen.has(id)) {
      skipped.push({ seed, id, status: "registered_already" });
      continue;
    }
    if (!ctx.catalog.has(predicateString(seed.predicate))) {
      skipped.push({ seed, id, status: "not_testable" });
      continue;
    }
    if (ctx.openCount + registered.length >= LEARN_MAX_OPEN_HYPOTHESES) {
      skipped.push({ seed, id, status: "open_cap" });
      continue;
    }
    const o = counts(threads, seed.predicate, seed.target);
    const lift = o.withN > 0 && o.withoutN > 0 ? (o.withWon / o.withN - o.withoutWon / o.withoutN) * 100 : 0;
    if (o.withN < LEARN_REGISTER_MIN_WITH || lift <= 0) {
      skipped.push({ seed, id, status: "no_signal" });
      continue;
    }
    const invalidators: Invalidator[] = [{ metric: "lift_pp", comparator: "lt", threshold: 0 }];
    if (complaintBase !== null) {
      invalidators.push({ metric: "complaint_rate", comparator: "gt", threshold: round1(complaintBase + LEARN_FLOOR_TOLERANCE_PP) });
    }
    seen.add(id);
    registered.push({
      id,
      clientId: ctx.clientId,
      category: seed.category,
      source: seed.source,
      sourceIds: [...seed.sourceIds],
      priority: seed.priority,
      predicate: seed.predicate,
      target: seed.target,
      scenarios: [...seed.scenarios],
      registeredWeek: week,
      registeredAt: ctx.now.toISOString(),
      predictedLiftPp: Math.max(1, Math.floor(lift)),
      invalidators,
      reviewBy: new Date(ctx.now.getTime() + expiry * DAY_MS).toISOString(),
      // TA-1361 fix round 1: a seed read on the replied population stores no ids. That population is every
      // closed first-reply day in the lookback and outgrows the row's 5000-id cap at about 60 new threads a day;
      // each of its threads began its reply before `now`, and the judge admits only a reply that began after
      // registration (`firstDecidedAt` is the run's start there), so none of them can confirm it.
      registrationThreadIds: onReplied ? [] : threads.map((t) => t.threadId).sort(),
      baseline,
    });
  }
  return { registered, skipped };
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** M3: an open hypothesis older than `HYPOTHESIS_MAX_OPEN_WEEKS` is closed as expired (a recorded decision, never a silent drop). */
export function hypothesisExpired(h: Hypothesis, now: Date): boolean {
  return now.getTime() - Date.parse(h.registeredAt) >= HYPOTHESIS_MAX_OPEN_WEEKS * 7 * DAY_MS;
}

/** G3: a hypothesis is never confirmed in the batch (week) that registered it. */
export function confirmable(h: Hypothesis, weekKey: string): { readonly ok: boolean; readonly status: ProposalStatus } {
  return h.registeredWeek < weekKey ? { ok: true, status: "registered" } : { ok: false, status: "same_week" };
}

// ---------------------------------------------------------------------------
// G4 to G6 evaluation
// ---------------------------------------------------------------------------

export interface Evaluation {
  readonly requiredN: number;
  readonly pooled: EvidenceCell;
  readonly cells: readonly EvidenceCell[];
  readonly status: ProposalStatus;
  readonly liftPp: number | null;
  readonly p: number | null;
  /** The closed, available, post-registration threads the verdict rests on. */
  readonly used: readonly ThreadOutcomeView[];
  /** Threads dropped because the predicate kind could not be read for them. */
  readonly excludedUnavailable: number;
  /** n of at least `LEARN_STRATUM_VALIDATE_MIN` on both sides in the pooled cell AND in every stratum (M7). */
  readonly fullyValidated: boolean;
}

const strataKey = (s: ThreadOutcomeView["stratum"]): string => `${s.inquiryType}|${s.priceBand}|${s.season}`;

function cellOf(stratum: ThreadOutcomeView["stratum"], withN: number, withWon: number, withoutN: number, withoutWon: number, min: number): EvidenceCell {
  const interval = newcombeDiffPp(withWon, withN, withoutWon, withoutN);
  return {
    stratum,
    withN,
    withWon,
    withoutN,
    withoutWon,
    wilson95Pp: interval === null ? null : [round1(interval[0]), round1(interval[1])],
    underpowered: withN < min || withoutN < min,
  };
}

/**
 * G4, G5 and the interval half of G6's sibling check. Evidence is only threads
 * closed AFTER `h.registeredAt` and decided (for `replied`, TA-1361: whose first
 * reply began after it, fix round 1, with its write-back day closed). The sample bar is
 * `patternMinSample` (the charter's copy of F-15's 5) times the tighten-only
 * `barMultiplier`, on BOTH sides: that many wins WITH the predicate (the
 * "5 consistent threads") and that many threads without it to compare with.
 */
export function evaluateHypothesis(h: Hypothesis, evidence: ThreadEvidence, c: LearningCharter, barMultiplier = 1): Evaluation {
  const requiredN = Math.ceil(c.bars.patternMinSample * Math.max(1, barMultiplier));
  const registeredMs = Date.parse(h.registeredAt);
  // TA-1361: the hypothesis's target picks its population, the same rule the registration and the monitor use.
  const threads = populationFor(evidence, h.target);
  // G3 (review B2): evidence is a thread that was NOT part of the registration evidence and whose
  // FIRST decided outcome came after registration, as well as one closed after it.
  const registration = new Set(h.registrationThreadIds ?? []);
  const closed = threads.filter(
    (t) => labelAdmits(t, h.target) && !registration.has(t.threadId) && Date.parse(t.closedAt) > registeredMs && Date.parse(t.firstDecidedAt ?? t.closedAt) > registeredMs,
  );
  const available = closed.filter((t) => !t.unavailable.includes(h.predicate.kind));
  const excludedUnavailable = closed.length - available.length;
  const target = predicateString(h.predicate);
  const emptyStratum = { inquiryType: "all", priceBand: "all", season: "all" } as const;

  // TA-1360: a thread with no reading for this target (a reply hypothesis, no shop reply) is in neither arm.
  const measured = available.filter((t) => measurableFor(t, h.target));
  const withG = measured.filter((t) => t.predicates.includes(target));
  const withoutG = measured.filter((t) => !t.predicates.includes(target));
  const won = (g: readonly ThreadOutcomeView[]): number => g.filter((t) => reachedTarget(t, h.target)).length;
  const pooled = cellOf(emptyStratum, withG.length, won(withG), withoutG.length, won(withoutG), requiredN);

  const base = { requiredN, pooled, used: measured, excludedUnavailable } as const;
  const noEvidence = { cells: [] as EvidenceCell[], liftPp: null, p: null, fullyValidated: false };

  if (closed.length > 0 && available.length === 0) return { ...base, ...noEvidence, status: "not_available" };
  if (pooled.withWon < requiredN || pooled.withoutN < requiredN) return { ...base, ...noEvidence, status: "insufficient_sample" };

  const byStratum = new Map<string, ThreadOutcomeView[]>();
  for (const t of measured) {
    const k = strataKey(t.stratum);
    const list = byStratum.get(k);
    if (list) list.push(t);
    else byStratum.set(k, [t]);
  }
  const cells: EvidenceCell[] = [];
  for (const group of [...byStratum.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, g]) => g)) {
    const first = group[0];
    if (!first) continue;
    const wg = group.filter((t) => t.predicates.includes(target));
    const og = group.filter((t) => !t.predicates.includes(target));
    cells.push(cellOf(first.stratum, wg.length, won(wg), og.length, won(og), LEARN_CELL_MIN_PER_SIDE));
  }

  const liftPp = (pooled.withWon / pooled.withN - pooled.withoutWon / pooled.withoutN) * 100;
  const p = oneSidedP(pooled.withWon, pooled.withN, pooled.withoutWon, pooled.withoutN);
  // M7: validated means EVERY stratum holds the bar on both sides, not only the pooled cell.
  const meets = (cell: EvidenceCell): boolean => cell.withN >= LEARN_STRATUM_VALIDATE_MIN && cell.withoutN >= LEARN_STRATUM_VALIDATE_MIN;
  const fullyValidated = meets(pooled) && cells.length > 0 && cells.every(meets);
  const common = { ...base, cells, liftPp, p, fullyValidated };

  // G5: a stratum with enough threads on both sides that REVERSES kills it.
  const reversed = cells.some((cell) => !cell.underpowered && cell.withWon / cell.withN < cell.withoutWon / cell.withoutN);
  if (reversed) return { ...common, status: "killed_stratum" };

  // Pooled interval must exclude zero on the positive side.
  const lo = pooled.wilson95Pp?.[0] ?? -Infinity;
  if (lo <= 0) return { ...common, status: "not_significant" };
  return { ...common, status: "evidence_ok" };
}

// ---------------------------------------------------------------------------
// G6 floors (B1)
// ---------------------------------------------------------------------------

export interface FloorBreach {
  readonly metric: FloorMetric;
  readonly observed: number;
  readonly bound: number;
  readonly comparator: "lt" | "gt";
}

export interface FloorReading {
  readonly breaches: readonly FloorBreach[];
  /** Metrics that could not be measured (no baseline, too few threads, no source). Said out loud, never read as passing. */
  readonly notMeasured: readonly FloorMetric[];
}

/**
 * Review B4: a proposal is HELD when the complaint or the escalation floor could not be measured.
 * Not measured is never read as passing; the other floors are said out loud on the proposal.
 */
export function floorsHold(r: FloorReading): boolean {
  return r.notMeasured.includes("complaint_rate") || r.notMeasured.includes("escalation_rate");
}

export function baselineOf(threads: readonly ThreadOutcomeView[]): FloorBaseline {
  const cont = threads.filter((t) => t.continued !== null);
  const rated = threads.filter((t) => t.reviewRating !== null);
  // G68: counted only over threads with a reading; the fields appear only when some thread was read for it.
  const read = threads.some((t) => t.aiTell !== undefined);
  const linted = threads.filter((t) => t.aiTell === true || t.aiTell === false);
  return {
    n: threads.length,
    complaints: threads.filter((t) => t.complaint).length,
    escalations: threads.filter((t) => t.escalated).length,
    continuationN: cont.length,
    continued: cont.filter((t) => t.continued === true).length,
    ratingN: rated.length,
    ratingSum: rated.reduce((s, t) => s + (t.reviewRating ?? 0), 0),
    ...(read ? { aiTellN: linted.length, aiTells: linted.filter((t) => t.aiTell === true).length } : {}),
  };
}

/**
 * Floors on a group of evidence threads against the frozen baseline (B1). The
 * charter's own floor `grounding_first_pass_percent` is read from the charter;
 * the rate floors compare to the baseline with `LEARN_FLOOR_TOLERANCE_PP`.
 * G68 (MS B14): the AI-tell rate is one of those rate floors, worse when it rises, read from
 * drafts.ai_tells; no reading on either side is not measured. R7: sentiment has no source, so it
 * is always listed not measured, said out loud on every proposal, never read as passing.
 */
export function checkFloors(group: readonly ThreadOutcomeView[], baseline: FloorBaseline | null, c: LearningCharter): FloorReading {
  const breaches: FloorBreach[] = [];
  const notMeasured: FloorMetric[] = [];
  const n = group.length;
  const g = baselineOf(group);

  const rate = (metric: FloorMetric, num: number, den: number, bNum: number | undefined, bDen: number | undefined, worse: "gt" | "lt"): void => {
    if (baseline === null || bNum === undefined || bDen === undefined || den < LEARN_CELL_MIN_PER_SIDE || bDen < LEARN_CELL_MIN_PER_SIDE) {
      notMeasured.push(metric);
      return;
    }
    const observed = (num / den) * 100;
    const ref = (bNum / bDen) * 100;
    const bound = worse === "gt" ? ref + LEARN_FLOOR_TOLERANCE_PP : ref - LEARN_FLOOR_TOLERANCE_PP;
    if (worse === "gt" ? observed > bound : observed < bound) breaches.push({ metric, observed: round1(observed), bound: round1(bound), comparator: worse === "gt" ? "gt" : "lt" });
  };
  rate("complaint_rate", g.complaints, n, baseline?.complaints, baseline?.n, "gt");
  rate("escalation_rate", g.escalations, n, baseline?.escalations, baseline?.n, "gt");
  rate("continuation_rate", g.continued, g.continuationN, baseline?.continued, baseline?.continuationN, "lt");
  rate("ai_tell_rate", g.aiTells ?? 0, g.aiTellN ?? 0, baseline?.aiTells, baseline?.aiTellN, "gt");

  if (baseline === null || g.ratingN < LEARN_CELL_MIN_PER_SIDE || baseline.ratingN < LEARN_CELL_MIN_PER_SIDE) notMeasured.push("review_rating");
  else {
    const observed = g.ratingSum / g.ratingN;
    const bound = baseline.ratingSum / baseline.ratingN - 0.25;
    if (observed < bound) breaches.push({ metric: "review_rating", observed: round1(observed), bound: round1(bound), comparator: "lt" });
  }

  const floor = c.floors.find((f) => f.id === "grounding_first_pass_percent");
  const measured = group.filter((t) => t.firstPassClean !== null);
  if (floor === undefined || floor.value === null || measured.length < LEARN_CELL_MIN_PER_SIDE) notMeasured.push("first_pass_grounding_percent");
  else {
    const observed = (measured.filter((t) => t.firstPassClean === true).length / measured.length) * 100;
    if (observed < floor.value) breaches.push({ metric: "first_pass_grounding_percent", observed: round1(observed), bound: floor.value, comparator: "lt" });
  }
  notMeasured.push("sentiment");
  return { breaches, notMeasured };
}

// ---------------------------------------------------------------------------
// G7 self-grill (B15.1, B15.2)
// ---------------------------------------------------------------------------

export type ConfoundVerdict = "clear" | "confounded" | "underpowered";

export interface CounterResult {
  readonly code: CounterCode;
  /** Cells the counter-hypothesis was tested on. */
  readonly tested: number;
  /** The counter-hypothesis was refuted: the lift kept its direction in every tested cell. */
  readonly killed: boolean;
  /** `dimension=value` of the first tested cell, or null when none was testable. */
  readonly cellRef: string | null;
}

export interface OwnerDirective {
  readonly id: string;
  /** The predicate the directive was mapped to (W5), or null when unmapped. */
  readonly predicate: Predicate | null;
  readonly category: string | null;
}

export interface SelfGrill {
  readonly confounds: { readonly contact: ConfoundVerdict; readonly season: ConfoundVerdict; readonly price_band: ConfoundVerdict; readonly inquiry_mix: ConfoundVerdict };
  readonly counters: readonly CounterResult[];
  readonly ifWrong: string;
  readonly ifRight: string;
  readonly collisions: { readonly t3: boolean; readonly directiveIds: readonly string[] };
  readonly invalidators: readonly Invalidator[];
  readonly unanswered: readonly SelfGrillQuestionId[];
  readonly retro: WinVerdict;
}

function tvd(a: readonly string[], b: readonly string[]): number {
  const keys = new Set([...a, ...b]);
  let sum = 0;
  for (const k of keys) {
    const pa = a.length === 0 ? 0 : a.filter((x) => x === k).length / a.length;
    const pb = b.length === 0 ? 0 : b.filter((x) => x === k).length / b.length;
    sum += Math.abs(pa - pb);
  }
  return sum / 2;
}

function mixVerdict(withG: readonly ThreadOutcomeView[], withoutG: readonly ThreadOutcomeView[], pick: (t: ThreadOutcomeView) => string, unmeasured: string | null): ConfoundVerdict {
  const a = withG.map(pick);
  const b = withoutG.map(pick);
  if (unmeasured !== null && [...a, ...b].every((v) => v === unmeasured)) return "underpowered";
  return tvd(a, b) > LEARN_MIX_TVD_MAX ? "confounded" : "clear";
}

/** Whether the lift keeps its sign inside each cell of `dimension` that has enough threads on both sides. */
function counterTest(
  code: CounterCode,
  dim: (t: ThreadOutcomeView) => string | null,
  label: string,
  used: readonly ThreadOutcomeView[],
  target: string,
  reach: (t: ThreadOutcomeView) => boolean,
): CounterResult {
  const groups = new Map<string, ThreadOutcomeView[]>();
  for (const t of used) {
    const v = dim(t);
    if (v === null) continue;
    const list = groups.get(v);
    if (list) list.push(t);
    else groups.set(v, [t]);
  }
  let tested = 0;
  let killed = true;
  let cellRef: string | null = null;
  for (const [v, g] of [...groups.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const wg = g.filter((t) => t.predicates.includes(target));
    const og = g.filter((t) => !t.predicates.includes(target));
    if (wg.length < LEARN_COUNTER_MIN_PER_SIDE || og.length < LEARN_COUNTER_MIN_PER_SIDE) continue;
    tested += 1;
    cellRef ??= `${label}=${v}`;
    const withRate = wg.filter(reach).length / wg.length;
    const withoutRate = og.filter(reach).length / og.length;
    if (withRate < withoutRate) killed = false;
  }
  return { code, tested, killed: tested > 0 && killed, cellRef };
}

export function ifWrongText(h: Hypothesis, ev: Evaluation): string {
  const lift = ev.liftPp === null ? "an unmeasured lift" : `a lift of ${round1(ev.liftPp)} points`;
  return `If this is wrong, the move would be applied to ${h.scenarios.join(", ") || "its scenarios"} on the strength of ${lift} that does not hold, and the owner's retraction at ${h.reviewBy.slice(0, 10)} is the backstop.`;
}

export function ifRightText(h: Hypothesis, ev: Evaluation): string {
  return `If this is right, threads with it reach the target ${ev.pooled.withWon} of ${ev.pooled.withN} times against ${ev.pooled.withoutWon} of ${ev.pooled.withoutN} without it, a predicted lift of ${h.predictedLiftPp} points.`;
}

/**
 * G7 and G8. Answers the charter's five questions from data. `directives` are
 * the owner directives W5 mapped to predicates (empty when none are known; the
 * owner's ratification is then the only check, recorded as such).
 */
export function selfGrill(h: Hypothesis, ev: Evaluation, directives: readonly OwnerDirective[]): SelfGrill {
  const target = predicateString(h.predicate);
  const used = ev.used.filter((t) => measurableFor(t, h.target));
  const reach = (t: ThreadOutcomeView): boolean => reachedTarget(t, h.target);
  const withG = used.filter((t) => t.predicates.includes(target));
  const withoutG = used.filter((t) => !t.predicates.includes(target));
  const withWins = withG.filter(reach);

  // Contact concentration, over the winning evidence (design: "one contact over 40% of evidence").
  const perContact = new Map<string, number>();
  for (const t of withWins) perContact.set(t.contactId, (perContact.get(t.contactId) ?? 0) + 1);
  const topContact = Math.max(0, ...perContact.values());
  const contact: ConfoundVerdict = withWins.length === 0 ? "underpowered" : topContact / withWins.length > LEARN_CONTACT_CONCENTRATION_MAX ? "confounded" : "clear";

  const confounds = {
    contact,
    season: mixVerdict(withG, withoutG, (t) => t.stratum.season, null),
    price_band: mixVerdict(withG, withoutG, (t) => t.stratum.priceBand, "no_amount"),
    inquiry_mix: mixVerdict(withG, withoutG, (t) => t.stratum.inquiryType, null),
  } as const;

  const topContactId = [...perContact.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0]?.[0] ?? null;
  const dropTop = used.filter((t) => t.contactId !== topContactId);
  const singleContact: CounterResult = (() => {
    if (topContactId === null || perContact.size < 2) return { code: "single_contact", tested: 0, killed: false, cellRef: null };
    const wg = dropTop.filter((t) => t.predicates.includes(target));
    const og = dropTop.filter((t) => !t.predicates.includes(target));
    if (wg.length < LEARN_COUNTER_MIN_PER_SIDE || og.length < LEARN_COUNTER_MIN_PER_SIDE) return { code: "single_contact", tested: 0, killed: false, cellRef: null };
    const holds = wg.filter(reach).length / wg.length > og.filter(reach).length / og.length;
    return { code: "single_contact", tested: 1, killed: holds, cellRef: "contact=top_removed" };
  })();
  const counters: CounterResult[] = [
    counterTest("season", (t) => t.stratum.season, "season", used, target, reach),
    counterTest("price_band", (t) => (t.stratum.priceBand === "no_amount" ? null : t.stratum.priceBand), "price_band", used, target, reach),
    singleContact,
    counterTest("inquiry_mix", (t) => t.stratum.inquiryType, "inquiry_type", used, target, reach),
    counterTest("response_speed", (t) => (t.fastResponse === null ? null : t.fastResponse ? "fast" : "slow"), "response_speed", used, target, reach),
    counterTest("owner_typed_not_agent", (t) => (t.ownerTyped === null ? null : t.ownerTyped ? "owner" : "agent"), "reply_author", used, target, reach),
  ];

  // Retro-grill (B4): the existing batch builder over the same evidence.
  const retroBatch = buildRetroGrillBatch({
    threads: used.map((t) => ({
      threadId: t.threadId,
      contactId: t.contactId,
      // TA-1360: the retro-grill counts a win as sold, booked or deposit. For `replied` the win is a write-back,
      // so each thread is shown to it as a sale or a loss by `reachedTarget`; a sales target passes its own outcome.
      outcome: h.target === "replied" ? (reach(t) ? "sold" : "lost") : t.outcome,
      closedAt: new Date(t.closedAt),
      lossHypothesis: null,
      candidateTrait: t.predicates.includes(target) ? target : null,
    })),
    periodStart: new Date(h.registeredAt),
    periodEnd: new Date(Math.max(Date.parse(h.registeredAt), ...used.map((t) => Date.parse(t.closedAt)))),
  });
  const verdicts = retroBatch.wins.filter((w) => w.candidateTrait !== null).map((w) => w.verdict);
  const retro: WinVerdict = verdicts.length === 0 ? "insufficient_data" : verdicts.includes("corroborated") ? "corroborated" : "isolated";

  const collidesT3 = directives.some((d) => d.category !== null && isT3OrUntiered(d.category));
  const directiveIds = directives
    .filter((d) => d.predicate !== null && predicateString(d.predicate) === target)
    .map((d) => d.id)
    .sort();
  const hypothesisT3 = isT3OrUntiered(h.category);

  const ifWrong = ifWrongText(h, ev);
  const ifRight = ifRightText(h, ev);
  const unanswered: SelfGrillQuestionId[] = [];
  if (!counters.some((k) => k.tested > 0 && k.killed)) unanswered.push("counter_hypothesis");
  if (ifWrong.trim() === "" || ifRight.trim() === "") unanswered.push("if_wrong_if_right");
  if (h.invalidators.length === 0) unanswered.push("invalidators");
  // `confounded` and `collides_owner_or_t3` are computed above and are always answered: a bad verdict is an answer.

  return {
    confounds,
    counters,
    ifWrong,
    ifRight,
    collisions: { t3: collidesT3 || hypothesisT3, directiveIds },
    invalidators: h.invalidators,
    unanswered,
    retro,
  };
}

function isT3OrUntiered(category: string): boolean {
  return Object.hasOwn(WRITE_TIERS, category) ? (WRITE_TIERS as Readonly<Record<string, string>>)[category] === "T3" : true;
}

// ---------------------------------------------------------------------------
// G9, G10 citations and routing
// ---------------------------------------------------------------------------

export interface Citations {
  readonly eventIds: readonly string[];
  readonly draftIds: readonly string[];
  readonly threadIds: readonly string[];
  readonly charterHash: string;
  readonly evalVersion: string | null;
  readonly evalVerdict: "beats" | "regressed" | "not_run";
  readonly charterRulesChecked: readonly string[];
}

export const CITATION_LIST_CAP = 50;

export function buildCitations(h: Hypothesis, ev: Evaluation, charter: { readonly hash: string; readonly label: string }, evalVersion: string | null): Citations {
  const target = predicateString(h.predicate);
  const wins = ev.used.filter((t) => measurableFor(t, h.target) && t.predicates.includes(target) && reachedTarget(t, h.target));
  const uniq = (xs: readonly string[]): string[] => [...new Set(xs)].sort().slice(0, CITATION_LIST_CAP);
  return {
    eventIds: uniq(wins.flatMap((t) => t.eventIds)),
    draftIds: uniq(wins.flatMap((t) => t.draftIds)),
    threadIds: uniq(wins.map((t) => t.threadId)),
    charterHash: charter.hash,
    evalVersion,
    evalVerdict: "not_run",
    charterRulesChecked: ["pre_registration", "pattern_min_sample", "self_grill", "floors", "write_tier", `charter:${charter.label}`],
  };
}

export interface Route {
  readonly tier: "T1" | "T2" | null;
  readonly status: ProposalStatus;
  /** What a T1 change still needs before anything could ship without a person (both are paid or live and wait for Kabir's typed go). */
  readonly requiredBeforeAutoship: readonly ("eval_regression" | "challenger_shadow")[];
}

const complete = (cites: Citations): boolean =>
  cites.threadIds.length > 0 && cites.draftIds.length + cites.eventIds.length > 0 && cites.charterHash !== "" && cites.charterRulesChecked.length > 0;

/**
 * G7 verdicts, G8 collisions, G9 citations, G10 route. T1 at client #1 is
 * `queued_owner`: owner-ratified provisional (F-15). No automatic pointer flip
 * exists; eval regression and challenger shadow are recorded as still required.
 */
export function routeProposal(h: Hypothesis, g: SelfGrill, cites: Citations): Route {
  const none = { tier: null, requiredBeforeAutoship: [] } as const;
  if (g.retro === "isolated") return { ...none, status: "isolated" };
  const confounded = Object.values(g.confounds).includes("confounded") || g.counters.some((k) => k.tested > 0 && !k.killed);
  if (confounded) return { ...none, status: "confounded" };
  if (g.unanswered.length > 0) return { ...none, status: "no_ship_unanswered" };
  if (g.collisions.t3 || g.collisions.directiveIds.length > 0) return { ...none, status: "held_collision" };
  if (!complete(cites)) return { ...none, status: "no_citations" };
  const tier = (WRITE_TIERS as Readonly<Record<string, "T1" | "T2" | "T3">>)[h.category];
  if (tier === "T2") return { tier: "T2", status: "queued_t2", requiredBeforeAutoship: [] };
  if (tier === "T1") return { tier: "T1", status: "queued_owner", requiredBeforeAutoship: ["eval_regression", "challenger_shadow"] };
  return { ...none, status: "held_collision" };
}

// ---------------------------------------------------------------------------
// G11 monitor (B7, B15.3)
// ---------------------------------------------------------------------------

export interface ShippedChange {
  readonly id: string;
  readonly kind: "pattern" | "artifact";
  readonly hypothesisId: string | null;
  readonly tier: "T1" | "T2";
  readonly predicate: Predicate | null;
  readonly target: HypothesisTarget;
  /** When it went live (the owner's ratification or approval). ISO. */
  readonly shippedAt: string;
  readonly reviewBy: string;
  readonly predictedLiftPp: number;
  readonly invalidators: readonly Invalidator[];
  /** Written by this learner (draft playbook version it proposed), so a floor breach may auto-revert it. */
  readonly learnerOriginated: boolean;
  /** For an artifact: the last version a person approved before this one. */
  readonly priorApprovedVersion: number | null;
  /**
   * G69 (MS B16 row 6, "can't move its own goalposts"): the floor baseline frozen on this change's
   * `hypothesis.registered` row. Null when the change has no hypothesis or the row carried none: its
   * floors then read not measured, never against a window the learner chose after shipping.
   */
  readonly baseline: FloorBaseline | null;
}

export type MonitorActionKind = "keep" | "retract" | "revert" | "freeze";

export interface MonitorAction {
  readonly changeId: string;
  readonly action: MonitorActionKind;
  /** A CODE. */
  readonly reason: string;
  readonly breaches: readonly FloorBreach[];
  readonly realizedLiftPp: number | null;
  readonly notMeasured: readonly FloorMetric[];
  /**
   * G69 fix round 1: the change carried no frozen baseline (`ShippedChange.baseline` null), so its rate
   * floors were not judged at all. A `keep` with this set is live without a floor check, and the monitor
   * handler must say so out loud (CLAUDE.md 0): one ops alert per run and change, keyed
   * `learn_no_frozen_baseline:<batchId>:<changeId>`, naming the change id and the reason code, and no
   * revert (nothing breached; nothing was measured). The kind is on no amber or owner list, so it pages
   * (alerts.ts). TA-1296 final review, core-1: the handler in packages/agents learnWeekly.ts skipped
   * every `keep` after integration and raised nothing; the alert is that handler's to add, not core's.
   */
  readonly noFrozenBaseline: boolean;
}

/**
 * Judges every live change on the threads closed since it shipped. Order:
 * floor breach (revert a learner-originated T1 artifact version, freeze a T2
 * one, retract a pattern), then a fired invalidator, then `reviewBy` passed.
 * Unprovable at `reviewBy` is retracted, never kept (precision beats recall).
 *
 * G69: each change's floors are read against ITS frozen baseline (`ShippedChange.baseline`), never
 * against a sliding window of the caller's (TA-1296 final review, core-3: the parameter that once
 * carried one is gone; a change with no frozen baseline is `noFrozenBaseline`, above).
 */
export function monitorShipped(
  live: readonly ShippedChange[],
  evidence: ThreadEvidence,
  now: Date,
  c: LearningCharter,
): MonitorAction[] {
  const out: MonitorAction[] = [];
  const required = LEARN_CELL_MIN_PER_SIDE;
  for (const ch of live) {
    const shippedMs = Date.parse(ch.shippedAt);
    // TA-1361: the change's target picks its population by the judge's rule, so its post-ship counts and the
    // baseline its hypothesis froze come from one population.
    const since = populationFor(evidence, ch.target).filter((t) => labelAdmits(t, ch.target) && Date.parse(t.closedAt) > shippedMs);
    const target = ch.predicate === null ? null : predicateString(ch.predicate);
    const exposed = target === null ? since : since.filter((t) => t.predicates.includes(target));
    const control = target === null ? [] : since.filter((t) => !t.predicates.includes(target));
    // TA-1361 fix round 1: only a baseline its own target's population froze; any other is none (`noFrozenBaseline`).
    const frozen = frozenBaselineFor(ch.baseline, ch.target);
    const floors = checkFloors(exposed, frozen, c);

    let realized: number | null = null;
    // TA-1360: the realized lift counts only threads measurable for the target, in both arms. The floors above
    // keep every exposed thread: a complaint on a thread that never got a reply is still a complaint.
    const exposedM = exposed.filter((t) => measurableFor(t, ch.target));
    const controlM = control.filter((t) => measurableFor(t, ch.target));
    if (target !== null && exposedM.length >= required && controlM.length >= required) {
      const w = exposedM.filter((t) => reachedTarget(t, ch.target)).length;
      const o = controlM.filter((t) => reachedTarget(t, ch.target)).length;
      realized = round1((w / exposedM.length - o / controlM.length) * 100);
    }
    const base = { changeId: ch.id, breaches: floors.breaches, realizedLiftPp: realized, notMeasured: floors.notMeasured, noFrozenBaseline: frozen === null } as const;

    if (floors.breaches.length > 0) {
      const reason = `floor_breach:${floors.breaches[0]?.metric ?? "unknown"}`;
      if (ch.kind === "artifact") {
        if (ch.tier === "T1" && ch.learnerOriginated && ch.priorApprovedVersion !== null) out.push({ ...base, action: "revert", reason });
        else out.push({ ...base, action: "freeze", reason });
      } else out.push({ ...base, action: "retract", reason });
      continue;
    }

    const fired = ch.invalidators.find((inv) => {
      if (inv.metric === "lift_pp") return realized !== null && (inv.comparator === "lt" ? realized < inv.threshold : realized > inv.threshold);
      const b = floors.breaches.find((x) => x.metric === inv.metric);
      return b !== undefined && (inv.comparator === "gt" ? b.observed > inv.threshold : b.observed < inv.threshold);
    });
    if (fired) {
      out.push({ ...base, action: ch.kind === "pattern" ? "retract" : "freeze", reason: `invalidator_fired:${fired.metric}` });
      continue;
    }

    if (now.getTime() >= Date.parse(ch.reviewBy)) {
      if (realized === null) out.push({ ...base, action: ch.kind === "pattern" ? "retract" : "freeze", reason: "review_by_passed_unproven" });
      else if (realized < ch.predictedLiftPp) out.push({ ...base, action: ch.kind === "pattern" ? "retract" : "freeze", reason: "review_by_passed_under_predicted" });
      else out.push({ ...base, action: "keep", reason: "review_by_passed_lift_held" });
      continue;
    }
    out.push({ ...base, action: "keep", reason: "within_review_window" });
  }
  return out;
}
